import { useRef, useEffect } from 'react';
import config from 'config';
import { Location } from 'core/types';
import { loadGoogleMapsScript } from './use-google-maps-loader';

type UsePlaceAutocompleteOptions = {
	onPlaceSelected?: (location: Location) => void;
	onError?: (error: unknown) => void;
	placeholder?: string;
};

export function usePlaceAutocomplete(options: UsePlaceAutocompleteOptions) {
	const { onPlaceSelected, onError, placeholder } = options;
	const containerRef = useRef<HTMLDivElement>(null);
	const elementRef =
		useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
	const placeSelectedCallbackRef = useRef(onPlaceSelected);
	const errorCallbackRef = useRef(onError);
	placeSelectedCallbackRef.current = onPlaceSelected;
	errorCallbackRef.current = onError;

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		let cancelled = false;

		const init = async () => {
			await loadGoogleMapsScript(config.google.placesApiKey);
			if (cancelled) return;

			const autocomplete =
				new google.maps.places.PlaceAutocompleteElement({
					types: ['geocode'],
					requestedLanguage: 'en',
				});

			elementRef.current = autocomplete;
			container.appendChild(autocomplete);

			// Set placeholder via the element's attribute since shadow DOM is closed
			if (placeholder) {
				autocomplete.setAttribute('placeholder', placeholder);
			}

			autocomplete.addEventListener('gmp-select', (async (
				event: Event
			) => {
				// Clear immediately so the selected text doesn't flash
				(autocomplete as unknown as { value: string }).value = '';

				// The runtime event has `placePrediction` (not `place`)
				// which must be converted via `.toPlace()` then fetched
				const selectEvent = event as Event & {
					placePrediction?: { toPlace(): google.maps.places.Place };
					place?: google.maps.places.Place;
				};

				const place =
					selectEvent.place ?? selectEvent.placePrediction?.toPlace();

				if (!place) {
					errorCallbackRef.current?.(
						new Error('Failed to get place details')
					);
					return;
				}

				try {
					await place.fetchFields({
						fields: [
							'displayName',
							'formattedAddress',
							'location',
							'addressComponents',
						],
					});
				} catch (err) {
					errorCallbackRef.current?.(err);
					console.error('Error fetching place details:', err);
					return;
				}

				const location: Location = {
					name: place.displayName ?? '',
					address: place.formattedAddress ?? '',
					latitude: place.location?.lat() ?? 0,
					longitude: place.location?.lng() ?? 0,
					zip: '',
					state: '',
					city: '',
					country: '',
				};

				if (place.addressComponents) {
					for (const component of place.addressComponents) {
						const types = component.types;
						if (types.includes('postal_code')) {
							location.zip = component.shortText ?? '';
						} else if (
							types.includes('administrative_area_level_1')
						) {
							location.state = component.shortText ?? '';
						} else if (types.includes('locality')) {
							location.city = component.shortText ?? '';
						} else if (types.includes('country')) {
							location.country = component.shortText ?? '';
						}
					}
				}

				placeSelectedCallbackRef.current?.(location);
			}) as EventListener);
		};

		init();

		return () => {
			cancelled = true;
			if (elementRef.current && container.contains(elementRef.current)) {
				container.removeChild(elementRef.current);
			}
			elementRef.current = null;
		};
	}, []);

	useEffect(() => {
		if (elementRef.current && placeholder) {
			elementRef.current.setAttribute('placeholder', placeholder);
		}
	}, [placeholder]);

	return { containerRef };
}
